import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/www/src/components/mdx/index.tsx";
import { graphql } from 'gatsby';
import { ComponentHeader } from 'components/thumbprint-components';
export const pageQuery = graphql`
    {
        # Get links to by path to display in the navbar.
        platformNav: allSitePage(filter: { path: { glob: "/components/avatar/*/" } }) {
            edges {
                node {
                    ...PlatformNavFragment
                }
            }
        }
    }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <ComponentHeader data={props.data} mdxType="ComponentHeader" />
    <h2>{`Avatar variations`}</h2>
    <p>{`Avatars can be placed in your layout files as either: `}<inlineCode parentName="p">{`EntityAvatarView`}</inlineCode>{` or `}<inlineCode parentName="p">{`UserAvatarView`}</inlineCode>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/avatar-android-variations.png",
        "alt": "Screenshot of both Avatar types"
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    android:id="@+id/variationEntity"
    style="@style/avatarExtraLarge" />
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    android:id="@+id/variationUser"
    style="@style/avatarExtraLarge"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`variationEntity.bind(imageUrl = NICOLAS_CAGE_URL, initials = "N")
variationUser.bind(imageUrl = NICOLAS_CAGE_URL, initials = "NC")
`}</code></pre>
    <p><inlineCode parentName="p">{`UserAvatarView`}</inlineCode>{` is for people or users whereas `}<inlineCode parentName="p">{`EntityAvatarView`}</inlineCode>{` is for companies, businesses, or services.`}</p>
    <p>{`Note that image URLs and avatar initials are `}<em parentName="p">{`not`}</em>{` specified in the layout; those are specified in the avatar’s `}<inlineCode parentName="p">{`.bind()`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`/**
* Begins loading the image from the given [imageUrl], positions and sizes the online badge,
* sets its visiblity based on [isOnline], and create a [BlankAvatarDrawable] as the
* fallback avatar. The [BlankAvatarDrawable] will contain the string [initials] and use the
* colors specified in [BlankAvatarDrawable.setColorsFromInitials], which maps the first letter
* of [initials] to background and text color values.
*
* If no [imageUrl] is given, or if there is an error fetching the image, then the
* [BlankAvatarDrawable] created from [initials] will be displayed. A gray placeholder is shown
* during the image's loading if an [imageUrl] is specified.
*
* If both [imageUrl] and [initials] are unspecified, no avatar will be displayed.
*
* As specified in Thumbprint style guides, entity avatars should pass in one letter
* for [initials] and user avatars should pass in two letters for [initials].
*/
fun bind(imageUrl: String? = null, initials: String? = null, isOnline: Boolean = false) {
    ...
}
`}</code></pre>
    <h2>{`Avatar sizes`}</h2>
    <p>{`Both `}<inlineCode parentName="p">{`UserAvatarView`}</inlineCode>{` and `}<inlineCode parentName="p">{`EntityAvatarView`}</inlineCode>{` are available in five sizes ranging from `}<inlineCode parentName="p">{`avatarExtraLarge`}</inlineCode>{` to `}<inlineCode parentName="p">{`avatarExtraSmall`}</inlineCode>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/android-avatar-sizes.png",
        "alt": "Avatar sizes"
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    style="@style/avatarExtraLarge"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    style="@style/avatarLarge"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    style="@style/avatarMedium"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    style="@style/avatarSmall"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.UserAvatarView
    style="@style/avatarExtraSmall"/>
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    style="@style/avatarExtraLarge"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    style="@style/avatarLarge"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    style="@style/avatarMedium"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    style="@style/avatarSmall"/>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<com.thumbtack.thumbprint.views.EntityAvatarView
    style="@style/avatarExtraSmall"/>
`}</code></pre>
    <h2>{`Avatars without images`}</h2>
    <p>{`Avatars without images can display the the user or entity’s initials instead. The initials and background colors are assigned based on the first letter in the `}<inlineCode parentName="p">{`initials`}</inlineCode>{` parameter of the `}<inlineCode parentName="p">{`.bind()`}</inlineCode>{` method.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/android-avatar-without-images.png",
        "alt": "Avatar Without Images"
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<LinearLayout
    android:id="@+id/noImagesUser"
    android:layout_width="wrap_content"
    android:layout_height="wrap_content"
    android:orientation="horizontal"
    android:gravity="center">

    <com.thumbtack.thumbprint.views.UserAvatarView
        style="@style/avatarMedium"/>
        ...

</LinearLayout>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<LinearLayout
    android:id="@+id/noImagesEntity"
    android:layout_width="wrap_content"
    android:layout_height="wrap_content"
    android:orientation="horizontal"
    android:gravity="center">

    <com.thumbtack.thumbprint.views.EntityAvatarView
        style="@style/avatarMedium"/>
        ...

</LinearLayout>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`var letters = sequenceOf("A", "B", "C", "D", "E", "F").iterator()
noImagesUser.forEachChild {
    (it as? UserAvatarView)?.bind(imageUrl = null, initials = letters.next() + "A")
}

letters = sequenceOf("A", "B", "C", "D", "E", "F").iterator()
noImagesEntity.forEachChild {
    (it as? EntityAvatarView)?.bind(imageUrl = null, initials = letters.next())
}
`}</code></pre>
    <h3>{`Online`}</h3>
    <p>{`This badge indicates that a user or entity is online. It can be set initially through the `}<inlineCode parentName="p">{`.bind()`}</inlineCode>{` method, or dynamically through the `}<inlineCode parentName="p">{`setIsOnline()`}</inlineCode>{` method.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/avatar-android-online-indicator.png",
        "alt": "Avatar badges"
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`badgesUser.forEachChild {
    (it as? UserAvatarView)?.bind(imageUrl = NICOLAS_CAGE_URL, isOnline = true)
}

badgesEntity.forEachChild {
    (it as? EntityAvatarView)?.bind(imageUrl = NICOLAS_CAGE_URL, isOnline = true)
}
`}</code></pre>


    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      